import * as mutationTypes from './mutation-types'
import api from '../api/check'
import { getCurrentUserName } from '@app/adapters/silverfin'
import { SfTime } from '@app/adapters/time'

const Checks = {
  state() {
    return {
      checkLists: {},
      checks: {},
      checkins: {},
      addCheckinPendingForCheckId: null,
    }
  },

  getters: {
    checkById: (state) => (id) => {
      return state.checks[id]
    },

    checkListChecks: (state) => (ledgerId, checkListId) => {
      return state.checkLists[ledgerId][checkListId].checkIds.map(
        (checkId) => state.checks[checkId]
      )
    },

    checkCheckins: (state, getters) => (checkId) => {
      return getters
        .checkById(checkId)
        .checkinIds.map((checkinId) => state.checkins[checkinId])
    },

    checkinIdByCheckAndUser: (state) => {
      return Object.entries(state.checks).reduce(
        (results, [checkId, check]) => {
          results[checkId] = check.checkinIds.reduce((acc, checkinId) => {
            const checkin = state.checkins[checkinId]

            if (checkin.anyUserId === null) {
              acc[checkin.userId] = checkin.id
              // make it as if the currently assigned user also checked this, even if it's a different original user
              acc[checkin.assignedUserId] = checkin.id
            }
            return acc
          }, {})
          return results
        },
        {}
      )
    },

    anyUserCheckinIdByCheckId: (state) => {
      return Object.entries(state.checks).reduce(
        (results, [checkId, check]) => {
          results[checkId] = check.checkinIds.reduce((acc, checkinId) => {
            const checkin = state.checkins[checkinId]

            if (checkin.anyUserId !== null) {
              acc.push(checkin.id)
            }

            return acc
          }, [])
          return results
        },
        {}
      )
    },

    checkinDateByCheckAndUser: (state) => {
      return Object.entries(state.checks).reduce(
        (results, [checkId, check]) => {
          results[checkId] = check.checkinIds.reduce((acc, checkinId) => {
            const checkin = state.checkins[checkinId]

            acc[checkin.userId || checkin.anyUserId] = checkin.createdAt
            acc[checkin.assignedUserId] = checkin.createdAt

            return acc
          }, {})
          return results
        },
        {}
      )
    },

    allChecks: (state) => {
      return state.checks
    },

    participantsByChecklist: (state) => {
      return Object.keys(state.checkLists).reduce((acc, ledgerId) => {
        const checkLists = state.checkLists[ledgerId]
        acc[ledgerId] = Object.keys(checkLists).reduce((acc2, checkListId) => {
          const checkList = checkLists[checkListId]
          const shouldCountAnyUser = checkList.allowAnyUser ? 1 : 0

          acc2[checkListId] =
            checkList.participantIds.length + shouldCountAnyUser
          return acc2
        }, {})
        return acc
      }, {})
    },
  },

  mutations: {
    [mutationTypes.SET_CHECK_LISTS](state, checkLists) {
      state.checkLists = checkLists
    },

    [mutationTypes.SET_CHECKS](state, checks) {
      state.checks = checks
    },

    [mutationTypes.TOGGLE_CHECK_NOT_APPLICABLE](state, { checkId, username }) {
      const existingCheck = state.checks[checkId]

      const nextMarkedNotApplicableBy = existingCheck.markedNotApplicableBy
        ? null
        : username

      state.checks[checkId] = {
        ...existingCheck,
        markedNotApplicableBy: nextMarkedNotApplicableBy,
        markedNotApplicableAt: nextMarkedNotApplicableBy
          ? SfTime().strftime('%-d %b %Y')
          : null,
      }
    },

    [mutationTypes.SET_CHECKINS](state, checkins) {
      state.checkins = checkins
    },

    [mutationTypes.ADD_CHECKIN_PENDING](state, value) {
      state.addCheckinPendingForCheckId = value
    },

    [mutationTypes.ADD_CHECKIN](state, { checkin, check }) {
      check.checkinIds.push(checkin.id)
      state.checkins[checkin.id] = checkin
    },

    [mutationTypes.DELETE_CHECKIN](state, { checkin, check }) {
      const index = check.checkinIds.indexOf(checkin.id)

      check.checkinIds.splice(index, 1)
      delete state.checkins[checkin.id]
    },

    [mutationTypes.SET_CHECK_FOLLOWER_IDS](state, { check, followerIds }) {
      // TODO: Verify the need for this bad practise
      check.followerIds = followerIds
    },
  },

  actions: {
    async toggleCheckLinkFollow({ commit, getters }, id) {
      const response = await api.toggleFollow(id)
      commit(mutationTypes.SET_CHECK_FOLLOWER_IDS, {
        check: getters.checkById(id),
        followerIds: response.followerIds,
      })
    },

    async toggleCheckNotApplicable({ commit, getters, state }, checkId) {
      commit(mutationTypes.TOGGLE_CHECK_NOT_APPLICABLE, {
        checkId,
        username: getCurrentUserName(),
      })

      const userId = parseInt(Silverfin.currentUserId())

      const userCheckinId =
        getters.checkinIdByCheckAndUser[checkId]?.[userId] ||
        getters.anyUserCheckinIdByCheckId[checkId]?.[0]

      if (userCheckinId) {
        const userCheckin = state.checkins[userCheckinId]

        commit(mutationTypes.DELETE_CHECKIN, {
          checkin: userCheckin,
          check: getters.checkById(checkId),
        })
      }
    },

    async addCheckin({ commit, getters, state, rootGetters }, checkId) {
      if (state.addCheckinPendingForCheckId === checkId) {
        return
      }

      commit(mutationTypes.ADD_CHECKIN_PENDING, checkId)

      const check = getters.checkById(checkId)
      const params = [checkId]

      // Inbox context - there is a communicationItem selected, so use its companyId,
      // otherwise, fallback to current company ID (by not providing a specific one)
      if (rootGetters.communicationItem) {
        params.push(rootGetters.communicationItem.companyId)
      }

      const response = await api.createCheckIn(...params)

      commit(mutationTypes.ADD_CHECKIN, { checkin: response, check: check })
      commit(mutationTypes.ADD_CHECKIN_PENDING, null)
    },

    async deleteCheckin({ commit, state, getters, rootGetters }, checkId) {
      const check = getters.checkById(checkId)
      const userId = parseInt(Silverfin.currentUserId())

      const userCheckinId =
        getters.checkinIdByCheckAndUser[checkId]?.[userId] ||
        getters.anyUserCheckinIdByCheckId[checkId]?.[0]

      if (!userCheckinId) {
        return
      }

      const params = [checkId, userCheckinId]

      // Inbox context - there is a communicationItem selected, so use its companyId,
      // otherwise, fallback to current company ID (by not providing a specific one)
      if (rootGetters.communicationItem) {
        params.push(rootGetters.communicationItem.companyId)
      }

      await api.deleteCheckIn(...params)

      commit(mutationTypes.DELETE_CHECKIN, {
        checkin: state.checkins[userCheckinId],
        check: check,
      })
    },
  },
}

export default Checks
